import React, { useCallback, useContext, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';

import useToast from '../../../../hooks/useToast';
import PodContext from '../../../../../context/PodContext';
import texts from './texts.json';

const CreateCoachPodModal = ({
  open, setOpen, loading, handlePodCreate,
}) => {
  const [podName, setPodName] = useState('');
  const [maxUsers, setMaxUsers] = useState(0);

  const { pods } = useContext(PodContext);
  const { showToast } = useToast();

  const handleClose = useCallback(() => {
    setOpen(false);
    setPodName('');
    setMaxUsers(0);
  }, [setOpen]);

  const createPod = useCallback(async () => {
    if (!podName.trim()) {
      return;
    }

    const podNameExists = pods.some((pod) => pod.name.toLowerCase() === podName.toLowerCase());

    if (podNameExists) {
      showToast(texts.podNameAlreadyExistsWarning, { error: true });
      return;
    }

    await handlePodCreate(podName, maxUsers);
    setPodName('');
    setMaxUsers(0);
    setOpen(false);
  }, [podName, pods, handlePodCreate, maxUsers, setOpen, showToast]);

  const handlePodNaming = (e) => {
    const { value } = e.target;
    const firstCharValid = value.length === 0 || /^[a-zA-Z]/.test(value);
    const entireStringValid = /^[a-zA-Z]([a-zA-Z0-9-]|\s(?!\s))*[a-zA-Z0-9-]?$/.test(value) || value === '';

    if (firstCharValid && entireStringValid) {
      setPodName(value);
    }
  };

  const handleMaxUsersChange = (event) => {
    const value = parseInt(event.target.value, 10) || 0;
    setMaxUsers(value);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle id="create-pod">
        {texts.createPodTitle}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {texts.createPodDescription}
        </DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          label="Pod Name"
          fullWidth
          variant="standard"
          onChange={handlePodNaming}
          value={podName}
          helperText={texts.createPodHelpText}
        />
        <TextField
          margin="dense"
          id="maxUsers"
          required
          label={texts.maxUsers}
          fullWidth
          variant="standard"
          type="number"
          inputProps={{
            min: 1,
            step: 1,
            pattern: '[1-9][0-9]*',
          }}
          size="small"
          onChange={handleMaxUsersChange}
          value={maxUsers === 0 ? '' : maxUsers}
          onKeyDown={(e) => {
            // Prevent decimal point, minus sign, 'e', and leading zeros
            if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E'
                      || (e.key === '0' && e.target.value === '')) {
              e.preventDefault();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{texts.cancel}</Button>
        <LoadingButton
          loading={loading}
          disabled={maxUsers === 0 || podName === '' || loading}
          variant="contained"
          onClick={createPod}
          autoFocus
        >
          {texts.create}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

CreateCoachPodModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handlePodCreate: PropTypes.func.isRequired,
};

export default CreateCoachPodModal;
